'use client';

import { useLayoutEffect, useState } from 'react';

/**
 * `aria-controls` / `aria-activedescendant` for a `role="combobox"` input
 * that drives a cmdk list (Combobox, Command Palette). cmdk doesn't expose
 * its item ids and its List may mount a pass later (in a portal), so follow
 * the list and its `aria-selected` option in the DOM.
 */
export function useCmdkActiveDescendant(listId: string | undefined, open: boolean) {
  const [ids, setIds] = useState<{ list?: string; active?: string }>({});
  useLayoutEffect(() => {
    const update = () => {
      const list = open && listId ? document.getElementById(listId) : null;
      const next = {
        list: list ? listId : undefined,
        active: list?.querySelector('[cmdk-item][aria-selected="true"]')?.id,
      };
      setIds((prev) =>
        prev.list === next.list && prev.active === next.active ? prev : next,
      );
    };
    update();
    if (!open) return;
    const observer = new MutationObserver(update);
    observer.observe(document.body, {
      subtree: true,
      childList: true,
      attributeFilter: ['aria-selected'],
    });
    return () => observer.disconnect();
  }, [open, listId]);
  return ids;
}
